import ReactSelect, { OptionTypeBase, ValueType } from 'react-select';
import * as React from 'react';
import { FieldError } from 'react-hook-form';
import { ISelectorOption } from 'types/ISelectorOption';
import customStyles, { errorStyles } from './styles';
import lastCustomStyles from './stylesCustomMenu';

export interface Props<T extends unknown> {
  id?: string;
  selectClassName?: string;
  options: ISelectorOption<T>[];
  multi?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  label?: string;
  onChange: (value?: T | T[]) => void;
  errors?: FieldError;
  isSearchBar?: boolean;
  value?: T | T[];
  isLastChild?: boolean;
  defaultValue?: ISelectorOption<T>;
  forceNullSelectedValue?: boolean;
  placeholder?: string;
}

const Select = <T extends unknown = unknown>({
  isSearchBar,
  id,
  options,
  multi = false,
  isClearable = false,
  isSearchable = false,
  label,
  onChange,
  errors,
  value,
  selectClassName,
  placeholder = 'Sélectionner...',
  isLastChild,
  defaultValue,
  forceNullSelectedValue = true,
}: Props<T>) => {
  React.useEffect(() => {
    if (!multi && value instanceof Array) onChange(undefined);
    // eslint-disable-next-line
  }, [multi, value]);

  // ✅ on garde la value technique, on change فقط le label affiché
  const mappedOptions = React.useMemo(() => {
    return (options || []).map((option) => {
      if (
        String(option?.label || '').toLowerCase() === 'hotel' ||
        String(option?.value || '').toLowerCase() === 'hotel'
      ) {
        return {
          ...option,
          label: 'Séjour',
        };
      }

      return option;
    });
  }, [options]);

  const mappedDefaultValue = React.useMemo(() => {
    if (!defaultValue) return defaultValue;

    if (
      String(defaultValue?.label || '').toLowerCase() === 'hotel' ||
      String(defaultValue?.value || '').toLowerCase() === 'hotel'
    ) {
      return {
        ...defaultValue,
        label: 'Séjour',
      };
    }

    return defaultValue;
  }, [defaultValue]);

  const selectedValue = React.useMemo(() => {
    if (!multi)
      return (
        mappedOptions?.find((el) => el.value === value) ||
        (forceNullSelectedValue ? null : undefined)
      );

    if (!(value instanceof Array))
      throw new Error('Value should be an array for multi = true');

    return mappedOptions.filter((el) => value.includes(el.value));
  }, [value, mappedOptions, multi, forceNullSelectedValue]);

  const handleChange = (event: ValueType<OptionTypeBase, boolean>): void => {
    if (!multi) return onChange((event as ISelectorOption<T> | null)?.value);

    return onChange((event as ISelectorOption<T>[] | null)?.map((el) => el.value) || []);
  };

  let styles = customStyles;

  if (isLastChild) {
    styles = lastCustomStyles;
  }

  return (
    <div
      className={`${isSearchBar ? 'my-0' : ''} form-group ${selectClassName} ${
        errors ? 'u-has-error' : ''
      }`}
    >
      {!isSearchBar && label && (
        <label className="form-label" htmlFor={id}>
          <span className="d-flex justify-content-between align-items-center">{label}</span>
        </label>
      )}

      <ReactSelect
        valueKey="value"
        placeholder={placeholder}
        isMulti={multi}
        options={mappedOptions}
        isClearable={isClearable}
        isSearchable={isSearchable}
        styles={errors ? errorStyles : styles}
        value={selectedValue}
        onChange={handleChange}
        defaultValue={mappedDefaultValue}
      />

      {errors && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {errors.message}
        </div>
      )}
    </div>
  );
};

export default Select;
